import React, { Component } from 'react';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

const ValidationTypes = ['onChange', 'onBlur', 'onSubmit'];

const isPromise = obj => Boolean(obj && typeof obj.then === 'function');

const validateField = (field, validator) => {
  return validator && validator(field);
};

const completeFieldValidation = (field, validators) => {
  const promises = [];
  let syncError;
  ValidationTypes.forEach(validationType => {
    if (!syncError) {
      const error = validateField(field, validators[validationType]);

      if (error) {
        if (isPromise(error)) {
          promises.push(error);
        } else {
          syncError = error;
        }
      }
    }
  });

  if (syncError) {
    return syncError;
  }

  if (promises.length > 0) {
    return new Promise(resolve => {
      Promise.all(promises).then(results => {
        resolve(results.find(Boolean));
      });
    });
  }
};

const copyData = data => {
  return {
    values: _objectSpread2({}, data.values),
    errors: _objectSpread2({}, data.errors),
    submitting: data.submitting
  };
};

class MorfiError extends Error {
  constructor(m, ...params) {
    super(m, ...params);
    Error.captureStackTrace && Error.captureStackTrace(this, MorfiError);
    this.name = 'MorfiError';
  }

}
const FormUtil = {
  isPromise,
  setSubmitting: (oldData, submitting) => {
    const result = copyData(oldData);
    result.submitting = submitting;
    return result;
  },
  update: (oldData, fieldId, value, error) => {
    const result = copyData(oldData);
    result.values[fieldId] = value;
    result.errors[fieldId] = error;
    return result;
  },
  validateField,
  completeFieldValidation,
  validateAll: (data, validation) => {
    const copy = copyData(data);
    const promises = [];
    Object.keys(validation).forEach(fieldId => {
      const error = completeFieldValidation(data.values[fieldId], validation[fieldId]);

      if (error) {
        if (isPromise(error)) {
          promises.push(error.then(err => {
            if (err) {
              copy.errors[fieldId] = err;
            }
          }));
        } else {
          copy.errors[fieldId] = error;
        }
      }
    });

    if (promises.length > 0) {
      return new Promise(resolve => {
        Promise.all(promises).then(() => resolve(copy));
      });
    }

    return copy;
  },
  hasErrors: data => Object.keys(data.errors).filter(key => data.errors[key] !== undefined).length > 0,
  isRequired: validators => {
    return !!validators && Boolean(completeFieldValidation(undefined, validators));
  },

  isValidationError(err) {
    return err instanceof Error && err.name === 'MorfiError';
  }

};

class Form extends Component {
  constructor(...args) {
    super(...args);

    _defineProperty(this, "update", (name, value, type) => {
      const {
        data,
        validation
      } = this.props;
      const validators = validation[name];
      const validator = validators ? validators[type] : undefined;
      const error = FormUtil.validateField(value, validator);

      if (FormUtil.isPromise(error)) {
        if (data.values[name] !== value) {
          this._onFieldChangeAfterValidation(name, value, undefined);
        }

        error.then(e => {
          if (this.props.data.values[name] === value) {
            this._onFieldChangeAfterValidation(name, value, e);
          }
        });
      } else {
        this._onFieldChangeAfterValidation(name, value, error);
      }
    });

    _defineProperty(this, "_onFieldChangeAfterValidation", (name, nextValue, nextError) => {
      const {
        data
      } = this.props;

      if (data.values[name] !== nextValue || nextError && !data.errors[name]) {
        this._onChange(FormUtil.update(data, name, nextValue, nextError));
      }
    });

    _defineProperty(this, "required", name => FormUtil.isRequired(this.props.validation[name]));

    _defineProperty(this, "_onSubmit", event => {
      event.preventDefault();
      const data = this.props.data;

      this._onChange(FormUtil.setSubmitting(data, true));

      const validated = FormUtil.validateAll(data, this.props.validation);

      if (FormUtil.isPromise(validated)) {
        validated.then(validatedData => {
          this._onSubmitAfterValidation(validatedData);
        });
      } else {
        this._onSubmitAfterValidation(validated);
      }
    });

    _defineProperty(this, "_onSubmitAfterValidation", data => {
      if (!FormUtil.hasErrors(data)) {
        const maybePromise = this.props.onSubmit(data.values);

        if (FormUtil.isPromise(maybePromise)) {
          maybePromise.then(this._finishSubmit).catch(e => {
            const nextData = FormUtil.setSubmitting(this.props.data, false);

            this._onChange(nextData);

            this._onSubmitFailed(e, nextData);
          });
        } else {
          this._finishSubmit();
        }
      } else {
        data.submitting = false;

        this._onChange(data);

        this._onSubmitFailed(new MorfiError('validation failed'), data);
      }
    });

    _defineProperty(this, "_finishSubmit", () => {
      const nextData = FormUtil.setSubmitting(this.props.data, false);

      this._onChange(nextData);

      this._onSubmitFinished(nextData);
    });

    _defineProperty(this, "_onChange", data => {
      if (this.m) this.props.onChange(data);
    });

    _defineProperty(this, "_onSubmitFailed", (e, data) => {
      const {
        onSubmitFailed
      } = this.props;
      if (this.m && onSubmitFailed) onSubmitFailed(e, data);
    });

    _defineProperty(this, "_onSubmitFinished", data => {
      const {
        onSubmitFinished
      } = this.props;
      if (this.m && onSubmitFinished) onSubmitFinished(data);
    });

    _defineProperty(this, "m", true);
  }

  componentWillUnmount() {
    this.m = false;
  }

  render() {
    const {
      className,
      children,
      data,
      FC,
      forwardRef
    } = this.props;
    return React.createElement("form", {
      className: className,
      onSubmit: this._onSubmit,
      method: "post",
      ref: forwardRef
    }, React.createElement(FC.Provider, {
      value: {
        data,
        update: this.update,
        required: this.required
      }
    }, children));
  }

}

const Field = ({
  value,
  error,
  name,
  children,
  update,
  required
}) => {
  return children({
    onChange: v => update(name, v, 'onChange'),
    onBlur: v => update(name, v, 'onBlur'),
    value,
    error,
    required: required(name)
  });
};

const makeField = (name, FC) => {
  const FieldUpdater = p => React.createElement(FC.Consumer, null, ({
    data,
    update,
    required
  }) => React.createElement(Field, {
    name: name,
    update: update,
    required: required,
    value: data.values[name],
    error: data.errors[name]
  }, p.children));

  return FieldUpdater;
};

const makeForm = FC => {
  const FormUpdater = p => React.createElement(Form, _extends({}, p, {
    FC: FC
  }));

  return FormUpdater;
};

const create = initial => {
  const FC = React.createContext({
    data: {
      values: initial,
      errors: {}
    }
  });
  const Form = makeForm(FC);
  const Fields = Object.keys(initial).reduce((r, name) => {
    r[name] = makeField(name, FC);
    return r;
  }, {});
  return {
    Form,
    Fields
  };
};

const Morfi = {
  create,
  hasErrors: FormUtil.hasErrors,
  isValidationError: FormUtil.isValidationError
};

export { FormUtil, Morfi, MorfiError, ValidationTypes };
